import { useState } from 'react';
import { usePostReactions } from '@/hooks/usePostReactions';
import { supabase } from '@/lib/supabase';

interface ReactionBarProps {
  postId: string;
  postTitle: string;
}

export default function ReactionBar({ postId, postTitle }: ReactionBarProps) {
  const { userReactions, counts, toggleReaction, loading } = usePostReactions(postId);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [copied, setCopied] = useState(false);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedTitle = encodeURIComponent(postTitle);
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(currentUrl).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
        return;
    }
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    setShowShareTooltip(false);
  };

  const handleReaction = async (type: 'like' | 'heart') => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      alert('Faça login com Google para interagir!');
      return;
    }
    await toggleReaction(type);
  };

  return (
    <div className="flex items-center gap-2 md:gap-3 flex-wrap">
      {/* Like button */}
      <button
        onClick={() => handleReaction('like')}
        disabled={loading}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap border ${
          userReactions.like
            ? 'bg-primary-100 border-primary-300 text-primary-600'
            : 'bg-background-100 border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-500'
        }`}
      >
        <i className={`${userReactions.like ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-base`}></i>
        <span>{counts.likes}</span>
      </button>

      {/* Heart button */}
      <button
        onClick={() => handleReaction('heart')}
        disabled={loading}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap border ${
          userReactions.heart
            ? 'bg-red-50 border-red-300 text-red-500'
            : 'bg-background-100 border-background-200 text-foreground-500 hover:border-red-300 hover:text-red-500'
        }`}
      >
        <i className={`${userReactions.heart ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
        <span>{counts.hearts}</span>
      </button>

      {/* Share button */}
      <div className="relative">
        <button
          onClick={() => setShowShareTooltip(!showShareTooltip)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap border bg-background-100 border-background-200 text-foreground-500 hover:border-foreground-300 hover:text-foreground-700"
        >
          <i className="ri-share-forward-line text-base"></i>
          <span className="hidden sm:inline">Compartilhar</span>
        </button>

        {showShareTooltip && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowShareTooltip(false)}></div>
            <div className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200 rounded-xl shadow-lg p-2 z-20 flex items-center gap-1">
              <button
                onClick={() => handleShare('facebook')}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-blue-100 text-foreground-600 hover:text-blue-600 transition-colors cursor-pointer"
                title="Facebook"
              >
                <i className="ri-facebook-circle-fill text-lg"></i>
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-sky-100 text-foreground-600 hover:text-sky-600 transition-colors cursor-pointer"
                title="Twitter/X"
              >
                <i className="ri-twitter-x-fill text-lg"></i>
              </button>
              <button
                onClick={() => handleShare('whatsapp')}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-green-100 text-foreground-600 hover:text-green-600 transition-colors cursor-pointer"
                title="WhatsApp"
              >
                <i className="ri-whatsapp-fill text-lg"></i>
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-foreground-100 text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
                title="Copiar link"
              >
                <i className={copied ? 'ri-check-line text-green-500' : 'ri-link text-lg'}></i>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}